/**
 * REK-2186: shared social OAuth buttons for consumer auth surfaces.
 *
 * Driven by `useEnabledSocialStrategies` (Clerk authenticatable set ∩ optional
 * `auth.social_providers` flag). Empty list → render nothing (email path
 * stays available). Admin surfaces must not mount this.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  useEnabledSocialStrategies,
  type EnabledSocialStrategy,
} from '@/hooks/useEnabledSocialStrategies';
import {
  startSocialAuthRedirect,
  normalizeOauthStrategy,
} from '@/lib/clerk-auth-flows';
import { GoogleLogo } from './GoogleLogo';

export interface SocialAuthButtonsProps {
  /**
   * Where Clerk returns after the provider round trip (SPA callback).
   * Relative paths resolve against the current origin.
   */
  redirectUrl?: string;
  /** Clerk `redirectUrlComplete` (required by Core 2; SPA completes manually). */
  redirectUrlComplete?: string;
  /** When true, buttons stay disabled until `termsAccepted`. */
  requireTermsAccepted?: boolean;
  termsAccepted?: boolean;
  /**
   * Called immediately before starting the redirect (e.g. persist the
   * 18+/ToS affirmation ISO for the pivot). Return false to abort.
   */
  onBeforeRedirect?: (strategy: `oauth_${string}`) => boolean | void;
  /** Called when the redirect fails to start (so the caller can clear stashed terms). */
  onStartFailed?: () => void;
  /**
   * Surface start failures to the parent (message + rate-limit flags from
   * `startSocialAuthRedirect`). Prefer this over parsing UI copy.
   */
  onError?: (error: {
    message: string;
    rateLimited: boolean;
    retryAfter: number | null;
  }) => void;
  disabled?: boolean;
  /** Visual density — login email stage vs primary pivot CTA. */
  variant?: 'primary' | 'outline';
  className?: string;
  /**
   * Render the shared "or" rule above the buttons. Only mounts when strategies
   * are visible — avoids an orphan divider when the list is empty.
   */
  leadingDivider?: boolean;
  /** Same as `leadingDivider`, but below the buttons (pivot demotion rule). */
  trailingDivider?: boolean;
}

function ProviderMark({
  strategy,
  name,
  logoUrl,
}: {
  strategy: string;
  name: string;
  logoUrl: string | null;
}) {
  if (strategy === 'oauth_google') {
    return (
      <span className="mr-2.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-white">
        <GoogleLogo className="size-4" />
      </span>
    );
  }
  if (logoUrl) {
    return (
      <span className="mr-2.5 flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
        <img src={logoUrl} alt="" className="size-4 object-contain" />
      </span>
    );
  }
  return (
    <span className="mr-2.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold uppercase text-foreground">
      {name.slice(0, 1)}
    </span>
  );
}

function labelFor(strategy: EnabledSocialStrategy, busy: boolean): string {
  if (busy) return `Connecting to ${strategy.name}…`;
  return `Continue with ${strategy.name}`;
}

function OrDivider() {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t" />
      </div>
      <div className="relative flex justify-center">
        <span className="bg-background px-3 text-sm text-muted-foreground">or</span>
      </div>
    </div>
  );
}

/**
 * Clerk is the sole auth engine (SSO Wave 3, REK-2166).
 * Must be rendered under `<ClerkProvider>`.
 * Returns null when no strategies are enabled.
 */
export function SocialAuthButtons(props: SocialAuthButtonsProps) {
  return <SocialAuthButtonsInner {...props} />;
}

function SocialAuthButtonsInner({
  redirectUrl = '/auth/sso-callback',
  redirectUrlComplete = '/onboarding',
  requireTermsAccepted = false,
  termsAccepted = true,
  onBeforeRedirect,
  onStartFailed,
  onError,
  disabled = false,
  variant = 'outline',
  className,
  leadingDivider = false,
  trailingDivider = false,
}: SocialAuthButtonsProps) {
  const { strategies, isLoading: strategiesLoading } = useEnabledSocialStrategies();
  const [busyStrategy, setBusyStrategy] = useState<string | null>(null);

  // Hide while empty — including during strategiesLoading — so leading/trailing
  // "or" dividers never flash alone above the email path before strategies arrive.
  if (strategies.length === 0) return null;

  const blocked =
    disabled ||
    strategiesLoading ||
    (requireTermsAccepted && !termsAccepted);

  const handleClick = async (strategy: EnabledSocialStrategy) => {
    if (blocked || busyStrategy) return;
    if (requireTermsAccepted && !termsAccepted) return;

    const normalized = normalizeOauthStrategy(strategy.strategy);
    if (!normalized) return;

    if (onBeforeRedirect?.(normalized) === false) return;

    setBusyStrategy(normalized);
    try {
      const result = await startSocialAuthRedirect({
        strategy: normalized,
        redirectUrl,
        redirectUrlComplete,
      });
      if (result.status === 'error') {
        onStartFailed?.();
        onError?.({
          message: result.error.message,
          rateLimited: result.error.rateLimited,
          retryAfter: result.error.retryAfter ?? null,
        });
        setBusyStrategy(null);
      }
      // redirecting: leave spinner up while the page unloads
    } catch (err) {
      onStartFailed?.();
      onError?.({
        message:
          err instanceof Error ? err.message : 'Failed to start social sign-in',
        rateLimited: false,
        retryAfter: null,
      });
      setBusyStrategy(null);
    }
  };

  // Disable every strategy button while any start is in flight (including the
  // busy one) — spinner/label convey progress; clicks stay no-ops either way.
  const anyBusy = busyStrategy !== null;

  return (
    <div className={className ?? 'space-y-3'}>
      {leadingDivider ? <OrDivider /> : null}
      {strategies.map((s) => {
        const busy = busyStrategy === s.strategy;
        return (
          <Button
            key={s.strategy}
            type="button"
            variant={variant === 'primary' ? 'default' : 'outline'}
            size="lg"
            className={
              variant === 'primary'
                ? 'w-full h-14 text-lg'
                : 'w-full h-12 text-base font-normal'
            }
            disabled={blocked || anyBusy}
            onClick={() => void handleClick(s)}
          >
            {busy ? (
              <>
                <Spinner className="size-5 mr-2" />
                {labelFor(s, true)}
              </>
            ) : (
              <>
                <ProviderMark
                  strategy={s.strategy}
                  name={s.name}
                  logoUrl={s.logoUrl}
                />
                {labelFor(s, false)}
              </>
            )}
          </Button>
        );
      })}
      {trailingDivider ? <OrDivider /> : null}
    </div>
  );
}
